import { Link } from "react-router";
import { Badge, type Tone } from "@/components/ui";
import { formatCompactMoney, formatMoney, formatStage } from "@/lib/format";
import type { CaseStage, RiskGrade } from "@/lib/types";
import { useCase } from "./case-context";

const STAGE_TONE: Record<CaseStage, Tone> = {
  data_collection: "caution",
  underwriting: "accent",
  approval: "positive",
  approved: "positive",
  rejected: "negative",
  closed: "neutral",
};

/** Grades below B carry visual weight; A and B do not need highlighting. */
function riskTone(grade: RiskGrade): Tone {
  if (grade.startsWith("A")) return "positive";
  if (grade.startsWith("C") || grade === "D") return "negative";
  return "neutral";
}

/**
 * The case identity strip. It sits above the tabs in the shared layout, so it
 * is on screen on every tab — the analyst never has to remember which case
 * they are in, and never loses the requested amount while reading evidence.
 */
export function CaseHeader() {
  const { case: kase, decision } = useCase();
  const borrowerHref = `/borrowers/${kase.borrowerId}`;

  return (
    <header className="border-b border-line bg-surface px-6 pt-5 pb-4">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-xl font-semibold tracking-tight text-ink">{kase.borrowerName}</h1>
            <Link
              to={borrowerHref}
              className="text-xs text-accent underline decoration-accent-line underline-offset-4 hover:decoration-accent"
            >
              Borrower profile
            </Link>
          </div>
          <p className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-ink-muted">
            <span>{kase.purpose}</span>
            <span aria-hidden className="text-ink-faint">
              ·
            </span>
            <span className="numeric">{kase.reference}</span>
            <span aria-hidden className="text-ink-faint">
              ·
            </span>
            <span>{kase.owner}</span>
          </p>
        </div>

        <div className="flex items-start gap-6">
          <div className="text-right">
            <div className="eyebrow">Requested</div>
            <div className="numeric mt-0.5 text-xl font-semibold" title={formatMoney(kase.requestedAmount)}>
              {formatCompactMoney(kase.requestedAmount)}
            </div>
          </div>
          <div className="flex flex-col items-end gap-1.5 pt-0.5">
            <Badge tone={STAGE_TONE[kase.stage]}>{formatStage(kase.stage)}</Badge>
            <Badge tone={riskTone(kase.riskGrade)}>Risk {kase.riskGrade}</Badge>
            {decision.outcome ? <Badge tone="accent">Decision recorded</Badge> : null}
          </div>
        </div>
      </div>
    </header>
  );
}
